import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Trash2, ShoppingCart, ArrowRight, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useWishlist } from '../contexts/WishlistContext';
import { useCart } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { calculateSalePrice } from '../utils/calculateSalePrice';

const Wishlist = () => {
  const { wishlistItems, wishlistCount, removeFromWishlist, clearWishlist, loading } = useWishlist();
  const { addItem } = useCart();
  const { formatPrice, currency } = useCurrency();

  const handleAddToCart = (product) => {
    addItem(product);
    toast.success(`${product.name} added to cart!`);
  };

  const handleMoveToCart = async (item) => {
    handleAddToCart(item.product);
    await removeFromWishlist(item.product_id, item.product.name);
  };

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />

      <div className="pt-44 pb-16 section-padding">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 animate-fade-in">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#0a0203] mb-2">
                My Wishlist
              </h1>
              <p className="text-gray-600">
                {wishlistCount} {wishlistCount === 1 ? 'item' : 'items'} saved for later
              </p>
            </div>
            {wishlistCount > 0 && (
              <Button
                onClick={clearWishlist}
                variant="outline"
                className="mt-4 md:mt-0 border-red-300 text-red-500 hover:bg-red-50"
                disabled={loading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Wishlist
              </Button>
            )}
          </div>

          {/* Wishlist Items */}
          {wishlistCount > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
              {wishlistItems.map((item) => (
                <div key={item.product_id} className="bg-white rounded-2xl overflow-hidden shadow-sm card-hover group">
                  <Link to={`/products/${item.product_id}`}>
                    <div className="aspect-square bg-gray-100 overflow-hidden relative">
                      <img 
                        src={item.product?.image_url} 
                        alt={item.product?.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      
                      {/* Badges */}
                      <div className="absolute top-3 left-3 flex flex-col gap-2">
                        {item.product?.size && (
                          <span className="bg-[#0a0203] text-white px-2 py-1 rounded-full text-xs font-medium">
                            {item.product.size}
                          </span>
                        )}
                        {calculateSalePrice(item.product, currency).isOnSale && (
                          <span className="text-white px-2 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#10b981' }}>
                            SALE
                          </span>
                        )}
                        {item.product?.is_affiliate_product && (
                          <span className="bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                            {item.product.affiliate_badge || 'Partner'}
                          </span>
                        )}
                      </div>
                      
                      {/* Remove button */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          removeFromWishlist(item.product_id, item.product?.name);
                        }}
                        className="absolute top-3 right-3 bg-white/90 text-red-500 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 hover:text-white"
                        disabled={loading}
                      >
                        <Heart className="h-4 w-4 fill-current" />
                      </button>
                    </div>
                  </Link>
                  
                  <div className="p-5">
                    {/* Rating */}
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-3.5 w-3.5 text-yellow-400 fill-current" />
                      ))}
                      <span className="text-xs text-gray-500 ml-1">(127)</span>
                    </div>
                    
                    <span className="text-xs text-[#003456] font-medium uppercase tracking-wider">
                      {item.product?.category}
                    </span>
                    <h3 className="font-semibold text-[#0a0203] mt-1 line-clamp-1">
                      {item.product?.name}
                    </h3>
                    
                    <div className="flex items-baseline gap-2 mt-2 mb-4">
                      {(() => {
                        const saleInfo = calculateSalePrice(item.product, currency);
                        if (saleInfo.isOnSale && saleInfo.effectivePrice) {
                          return (
                            <>
                              <span className="text-xl font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono' }}>
                                {formatPrice(saleInfo.effectivePrice)}
                              </span>
                              <span className="text-sm text-gray-400 line-through">
                                {formatPrice(item.product.price)}
                              </span>
                            </>
                          );
                        }
                        return (
                          <span className="text-xl font-bold text-[#003456]" style={{ fontFamily: 'JetBrains Mono' }}>
                            {formatPrice(item.product?.price)}
                          </span>
                        );
                      })()}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleMoveToCart(item)}
                        className="flex-1 bg-[#003456] hover:bg-[#001d30] text-white"
                        disabled={loading || !item.product?.in_stock}
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        {item.product?.in_stock ? 'Add to Cart' : 'Out of Stock'}
                      </Button>
                      <Button
                        onClick={() => removeFromWishlist(item.product_id, item.product?.name)}
                        variant="outline"
                        className="border-gray-300"
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-16 text-center shadow-sm animate-fade-in">
              <div className="w-20 h-20 mx-auto mb-6 bg-[#dbeef5] rounded-full flex items-center justify-center">
                <Heart className="h-10 w-10 text-[#003456]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0a0203] mb-2">
                Your wishlist is empty
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Save items you love by clicking the heart icon on any product. Your wishlist will be waiting for you when you're ready.
              </p>
              <Link to="/products">
                <Button className="bg-[#003456] hover:bg-[#001d30] text-white">
                  Browse Products
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {/* Continue Shopping CTA */}
          {wishlistCount > 0 && (
            <div className="mt-12 bg-gradient-to-r from-[#003456] to-[#001d30] rounded-2xl p-8 md:p-12 text-center text-white">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Ready to Complete Your Purchase?
              </h2>
              <p className="text-white/80 mb-6 max-w-xl mx-auto">
                Your favorite items are waiting. Add them to your cart and checkout today.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/products">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456]">
                    Continue Shopping
                  </Button>
                </Link>
                <Link to="/cart">
                  <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203]">
                    View Cart
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Wishlist;
